/**
 * PATH: src/pages/pricing/AdminPricingPage.jsx
 * PURPOSE: Admin CRUD management for pricing plans
 */

import React, { useEffect, useState } from "react";
import axios from "../../utils/axiosInstance";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Switch,
  Dialog,
  TextField,
  Stack
} from "@mui/material";

export default function AdminPricingPage() {
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);

  /* =====================================================
     LOAD PLANS
  ===================================================== */
  const loadPlans = async () => {
    const res = await axios.get("/api/billing/plans/admin/all");
    setPlans(res.data);
  };

  useEffect(() => {
    loadPlans();
  }, []);

  /* =====================================================
     SAVE PLAN (FIXED NUMERIC + ARIA ISSUE)
  ===================================================== */
  const handleSave = async () => {
    if (!editingPlan) return;

    const payload = {
      ...editingPlan,
      price:
        editingPlan.price === "" || editingPlan.price === null
          ? 0
          : Number(editingPlan.price)
    };

    try {
      if (editingPlan._id) {
        await axios.put(
          `/api/billing/plans/${editingPlan._id}`,
          payload
        );
      } else {
        await axios.post("/api/billing/plans", payload);
      }

      // 🔥 FIX aria-hidden focus issue
      if (document.activeElement) {
        document.activeElement.blur();
      }

      setOpen(false);
      setEditingPlan(null);
      loadPlans();
    } catch (err) {
      console.error("Plan save failed:", err);
    }
  };

  /* =====================================================
     DELETE
  ===================================================== */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this plan?")) return;
    await axios.delete(`/api/billing/plans/${id}`);
    loadPlans();
  };

  return (
    <Box p={4}>
      <Typography variant="h4" fontWeight={700} mb={3}>
        Admin – Pricing Plans
      </Typography>

      <Button
        variant="contained"
        onClick={() => {
          setEditingPlan({
            name: "",
            code: "",
            price: "", // ✅ FIXED (was 0)
            duration: "monthly",
            revenueStream: "user_subscription",
            isActive: true,
            features: []
          });
          setOpen(true);
        }}
      >
        + Create Plan
      </Button>

      <Table sx={{ mt: 3 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Stream</TableCell>
            <TableCell>Price (₹)</TableCell>
            <TableCell>Duration</TableCell>
            <TableCell>Active</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {plans.map((p) => (
            <TableRow key={p._id}>
              <TableCell>{p.name}</TableCell>
              <TableCell>{p.revenueStream}</TableCell>
              <TableCell>{p.price}</TableCell>
              <TableCell>{p.duration}</TableCell>
              <TableCell>
                <Switch
                  checked={p.isActive}
                  onChange={async () => {
                    await axios.put(`/api/billing/plans/${p._id}`, {
                      ...p,
                      price: Number(p.price),
                      isActive: !p.isActive
                    });
                    loadPlans();
                  }}
                />
              </TableCell>
              <TableCell>
                <Button
                  onClick={() => {
                    setEditingPlan({
                      ...p,
                      price: p.price ?? "" // ✅ prevent leading zero bug
                    });
                    setOpen(true);
                  }}
                >
                  Edit
                </Button>
                <Button
                  color="error"
                  onClick={() => handleDelete(p._id)}
                >
                  Delete
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* =====================================================
         EDIT / CREATE DIALOG
      ===================================================== */}
      <Dialog
        open={open}
        onClose={() => {
          if (document.activeElement) {
            document.activeElement.blur();
          }
          setOpen(false);
        }}
        maxWidth="sm"
        fullWidth
      >
        <Box p={3}>
          <Typography variant="h6" mb={2}>
            {editingPlan?._id ? "Edit Plan" : "Create Plan"}
          </Typography>

          <Stack spacing={2}>
            <TextField
              label="Name"
              value={editingPlan?.name || ""}
              onChange={(e) =>
                setEditingPlan({ ...editingPlan, name: e.target.value })
              }
            />

            <TextField
              label="Code"
              value={editingPlan?.code || ""}
              onChange={(e) =>
                setEditingPlan({ ...editingPlan, code: e.target.value })
              }
            />

            <TextField
              label="Price (INR)"
              type="number"
              value={editingPlan?.price ?? ""}
              onChange={(e) =>
                setEditingPlan({
                  ...editingPlan,
                  price:
                    e.target.value === ""
                      ? ""
                      : Number(e.target.value)
                })
              }
              inputProps={{ min: 0 }}
            />

            <TextField
              label="Revenue Stream"
              value={editingPlan?.revenueStream || ""}
              onChange={(e) =>
                setEditingPlan({
                  ...editingPlan,
                  revenueStream: e.target.value
                })
              }
            />

            <Button variant="contained" onClick={handleSave}>
              Save
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </Box>
  );
}
